import { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/authcontext';
import EmailService, { MailProvider } from '../feature-module/application/components/email/email.service';

interface UseEmailProvidersReturn {
  hasConnectedProviders: boolean;
  providers: MailProvider[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useEmailProviders = (): UseEmailProvidersReturn => {
  const { auth, selectedTenantId } = useContext(AuthContext);
  const initialUserId = auth?.user_id?.toString() || '';
  const initialCached = initialUserId ? EmailService.getCachedProviders(initialUserId) : null;
  const [providers, setProviders] = useState<MailProvider[]>(initialCached || []);
  const [loading, setLoading] = useState(!initialCached);
  const [error, setError] = useState<string | null>(null);

  const fetchProviders = async () => {
    try {
      setLoading(true);
      setError(null);

      const userId = auth?.user_id?.toString() || '';
      if (!userId) {
        setProviders([]);
        return;
      }

      const providersData = await EmailService.getMailProviders(userId);
      setProviders(providersData || []);
    } catch (err) {
      console.error('Error fetching email providers:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch email providers');
      setProviders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const userId = auth?.user_id?.toString() || '';
    if (!userId) {
      setLoading(false);
      setProviders([]);
      return;
    }

    const cached = EmailService.getCachedProviders(userId);
    if (cached) {
      setProviders(cached);
      setLoading(false);
      return;
    }

    fetchProviders();
  }, [auth?.user_id, selectedTenantId]);

  return {
    hasConnectedProviders: providers.length > 0,
    providers,
    loading,
    error,
    refetch: fetchProviders
  };
}; 